/**
 * Which screens an account may navigate to — the permission behind each entry
 * in the navigation map, and nothing else.
 *
 * It lives here rather than in `src/components/layout/nav-items.ts` because
 * that module carries the icons and the Hebrew labels, and this is a rule: the
 * sidebar, the phone's tab bar and the "עוד" hub all ask the same question, and
 * they must all get the same answer. The answer itself is still
 * `hasPermission()`'s alone — this only says which action each screen stands
 * behind.
 *
 * Most screens stand behind nothing (`null`) and are navigable by anybody with
 * an account. The screens inside those screens are guarded on their own: a נהג
 * reaches the roster and finds every editing control refused, which is a
 * different thing from not reaching it. The two entries that *are* gated here
 * are the two the directory itself turns on:
 *
 * - **הגדרות** — `MANAGE_SYSTEM_SETTINGS`
 * - **ניהול משתמשים** — `MANAGE_USERS`
 *
 * Both are super-admin-only in `src/core/permissions.ts`, which is the level a
 * D1 `admin` is enforced at. A `manager` (מפקד / בעל תפקיד) therefore never sees
 * either tab — and the server refuses the routes behind them regardless, in
 * `functions/api/_middleware.js`. Hiding a screen is a courtesy; the middleware
 * is the rule.
 */
import { hasPermission } from './permissions';
import type { User, UserPermission } from '../types/user';

/** Every screen the shell can navigate to. */
export const NAV_SCREEN_IDS = [
  'home',
  'tasks',
  'vehicles',
  'soldiers',
  'reports',
  'users',
  'settings',
  'more',
] as const;

export type NavScreenId = (typeof NAV_SCREEN_IDS)[number];

/** The permission each screen stands behind — `null` for "anybody signed in". */
export const NAV_PERMISSIONS: Record<NavScreenId, UserPermission | null> = {
  home: null,
  tasks: null,
  vehicles: null,
  soldiers: null,
  reports: null,
  users: 'MANAGE_USERS',
  settings: 'MANAGE_SYSTEM_SETTINGS',
  more: null,
};

/** Whether this account may navigate to that screen. */
export function canSeeNavItem(user: User | null | undefined, id: NavScreenId): boolean {
  const required = NAV_PERMISSIONS[id];
  if (required === null) return Boolean(user);
  return hasPermission(user, required);
}

/** The screens this account may navigate to, in the order they were given. */
export function visibleNavIds<T extends NavScreenId>(
  user: User | null | undefined,
  ids: readonly T[] = NAV_SCREEN_IDS as readonly NavScreenId[] as readonly T[],
): T[] {
  return ids.filter((id) => canSeeNavItem(user, id));
}

/**
 * The screen to actually open.
 *
 * A restored view (`src/core/session-cache.ts`) is a screen the *previous*
 * operator on this installation was on. Signing in as somebody narrower must
 * not drop them onto it, so a screen this account may not see falls back to the
 * dashboard rather than rendering an empty page or, worse, a populated one.
 */
export function resolveNavId(
  user: User | null | undefined,
  requested: NavScreenId | null | undefined,
): NavScreenId {
  if (requested && canSeeNavItem(user, requested)) return requested;
  return 'home';
}
